import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BedtimeRoundedIcon from "@mui/icons-material/BedtimeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import LocalDiningRoundedIcon from "@mui/icons-material/LocalDiningRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import { compactActionSx, surfaceSx } from "./center/ownerCenterUi";

const DAY_LABELS = ["D", "L", "M", "X", "J", "V", "S"];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const WINDOW_LABELS = {
  any: "En cualquier momento",
  morning: "Mañana",
  midday: "Mediodía",
  afternoon: "Tarde",
  evening: "Tarde/noche",
  night: "Noche",
};
const KIND_LABELS = {
  duration: "Acumular tiempo",
  event: "Que ocurra una vez",
  wake_before: "Levantarme antes de una hora",
  count: "Cantidad por día",
  sleep: "Sueño",
};

const MEAL_LABELS = { breakfast: "Desayuno", lunch: "Almuerzo", snack: "Merienda", dinner: "Cena", other: "Comida" };

function mealClock(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("es-AR", { hour: "2-digit", minute: "2-digit" }).format(date);
}

function NutritionPanel({ nutrition }) {
  const today = nutrition?.today || {};
  const byType = today.byType || {};
  const foods = nutrition?.foods || [];
  const history = nutrition?.history || [];
  const mainMeals = ["breakfast", "lunch", "dinner"];
  return (
    <Box id="owner-life-nutrition" sx={{ ...surfaceSx, p: { xs: 1.35, sm: 1.55 } }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "flex-start" }}>
        <Box>
          <Stack direction="row" spacing={.65} alignItems="center">
            <RestaurantRoundedIcon fontSize="small" />
            <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Alimentación</Typography>
          </Stack>
          <Typography sx={{ mt: .35, color: "text.secondary", fontSize: 12.5, lineHeight: 1.45, maxWidth: 720 }}>
            Jarvis registra lo que comiste hablando. Las calorías son estimaciones orientativas salvo que hayas confirmado un alimento en tu memoria.
          </Typography>
        </Box>
        <Chip size="small" icon={<AutoAwesomeRoundedIcon />} label="Memoria de comidas" variant="outlined" sx={{ fontWeight: 850 }} />
      </Stack>

      <Box sx={{ mt: 1.2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3,minmax(0,1fr))" }, gap: .8 }}>
        {mainMeals.map((type) => {
          const meal = byType[type];
          const skipped = meal?.status === "skipped";
          return (
            <Box key={type} sx={{ p: 1.05, border: 1, borderColor: "divider", borderRadius: 2.2, minWidth: 0 }}>
              <Typography sx={{ color: "text.secondary", fontSize: 10.8, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".07em" }}>{MEAL_LABELS[type]}</Typography>
              {!meal ? <Typography sx={{ mt: .45, fontWeight: 900, fontSize: 13.2 }}>Sin registrar</Typography> : skipped ? (
                <Typography sx={{ mt: .45, fontWeight: 900, fontSize: 13.2 }}>Salteado</Typography>
              ) : (
                <>
                  <Typography sx={{ mt: .45, fontWeight: 950, fontSize: 13.2, lineHeight: 1.35 }}>{meal.description || "Registrado"}</Typography>
                  <Typography sx={{ mt: .3, color: "text.secondary", fontSize: 11.5, fontWeight: 800 }}>
                    {mealClock(meal.occurredAt)}{Number.isFinite(Number(meal.caloriesEstimate)) && Number(meal.caloriesEstimate) > 0 ? ` · ~${Math.round(Number(meal.caloriesEstimate))} kcal` : ""}
                  </Typography>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ mt: .9, display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(4,minmax(0,1fr))" }, gap: .75 }}>
        {[
          ["Comidas hoy", Number(today.eatenCount || 0), <LocalDiningRoundedIcon fontSize="small" />],
          ["Salteadas", Number(today.skippedCount || 0), <ScheduleRoundedIcon fontSize="small" />],
          ["Kcal estimadas", Number(today.caloriesEstimate || 0) || "—", <InsightsRoundedIcon fontSize="small" />],
          ["Promedio con datos", nutrition?.stats?.avgEstimatedCalories ? `~${nutrition.stats.avgEstimatedCalories}` : "—", <TrackChangesRoundedIcon fontSize="small" />],
        ].map(([label, value, icon]) => (
          <Box key={label} sx={{ p: .9, bgcolor: "action.hover", borderRadius: 2 }}>
            <Stack direction="row" spacing={.5} alignItems="center" sx={{ color: "text.secondary" }}>{icon}<Typography sx={{ fontSize: 10.7, fontWeight: 900 }}>{label}</Typography></Stack>
            <Typography sx={{ mt: .25, fontWeight: 1000, fontSize: 18 }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      {history.some((row) => row.meals > 0) ? (
        <Box sx={{ mt: 1.1 }}>
          <Typography sx={{ fontWeight: 950, fontSize: 12.5 }}>Últimos días</Typography>
          <Stack direction="row" spacing={.55} useFlexGap flexWrap="wrap" sx={{ mt: .65 }}>
            {history.filter((row) => row.meals > 0).slice(-7).map((row) => (
              <Chip key={row.dateKey} size="small" label={`${row.dateKey.slice(5)} · ${row.eaten} comida${row.eaten === 1 ? "" : "s"}${row.calorieEntries ? ` · ~${row.caloriesEstimate} kcal` : ""}`} variant="outlined" sx={{ fontWeight: 800 }} />
            ))}
          </Stack>
        </Box>
      ) : null}

      {foods.length ? (
        <Box sx={{ mt: 1.15 }}>
          <Divider sx={{ mb: 1 }} />
          <Typography sx={{ fontWeight: 950, fontSize: 12.5 }}>Comidas que Jarvis ya conoce de vos</Typography>
          <Typography sx={{ mt: .2, color: "text.secondary", fontSize: 11.5 }}>Cuando reaparecen, Jarvis reutiliza tu memoria antes de volver a estimar.</Typography>
          <Stack direction="row" spacing={.55} useFlexGap flexWrap="wrap" sx={{ mt: .7 }}>
            {foods.slice(0, 10).map((food) => (
              <Chip
                key={food.id || food.normalizedKey}
                size="small"
                label={`${food.name}${Number.isFinite(Number(food.caloriesEstimate)) ? ` · ~${Math.round(Number(food.caloriesEstimate))} kcal` : ""}${food.timesSeen > 1 ? ` · ${food.timesSeen}×` : ""}`}
                variant={food.verified ? "filled" : "outlined"}
                sx={{ fontWeight: 800 }}
              />
            ))}
          </Stack>
        </Box>
      ) : null}

      <Typography sx={{ mt: 1.05, color: "text.secondary", fontSize: 11.2, lineHeight: 1.4 }}>
        Probá diciéndole a Jarvis: “no desayuné”, “almorcé a las 15 una milanesa con ensalada” o “cené pizza”. No hace falta entrar a un formulario.
      </Typography>
    </Box>
  );
}


function sleepDurationLabel(minutes) {
  const value = Math.max(0, Math.round(Number(minutes || 0)));
  if (!value) return "Sin datos suficientes";
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function SleepPanel({ sleep, onConfigure, onActivate }) {
  const objective = sleep?.objective || {};
  const parts = [];
  if (objective.targetTime) parts.push(`acostarme ${objective.targetTime}`);
  if (objective.targetMinutes) parts.push(`dormir ${sleepDurationLabel(objective.targetMinutes)}`);
  const open = sleep?.session?.state === "open";
  return (
    <Box id="owner-life-sleep" sx={{ ...surfaceSx, p: { xs: 1.35, sm: 1.55 } }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "flex-start" }}>
        <Box>
          <Stack direction="row" spacing={.65} alignItems="center">
            <BedtimeRoundedIcon fontSize="small" />
            <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Sueño</Typography>
          </Stack>
          <Typography sx={{ mt: .35, color: "text.secondary", fontSize: 12.5, lineHeight: 1.45 }}>
            Realidad de descanso basada sólo en lo que registrás: acostarte + el mismo despertar que ya usa Jarvis.
          </Typography>
        </Box>
        <Stack direction="row" spacing={.6}>
          {sleep?.habit && objective.active === false ? <Button variant="outlined" onClick={() => onActivate(sleep.habit)} sx={compactActionSx}>Activar</Button> : null}
          <Button variant="outlined" onClick={onConfigure} sx={compactActionSx}>{sleep?.habit ? "Editar objetivo" : "Configurar objetivo"}</Button>
        </Stack>
      </Stack>
      <Box sx={{ mt: 1.1, display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(4,minmax(0,1fr))" }, gap: .75 }}>
        {[
          ["Objetivo", objective.configured ? parts.join(" · ") : "Sin objetivo"],
          ["Acostarse", sleep?.lastBedtime?.clock || "Sin registrar"],
          ["Levantarse", sleep?.wake?.clock || "Sin registrar"],
          ["Duración", open ? "Sesión abierta" : sleepDurationLabel(sleep?.durationMinutes)],
        ].map(([label, value]) => (
          <Box key={label} sx={{ p: .95, bgcolor: "action.hover", borderRadius: 2 }}>
            <Typography sx={{ color: "text.secondary", fontSize: 10.7, fontWeight: 900 }}>{label}</Typography>
            <Typography sx={{ mt: .3, fontWeight: 950, fontSize: 13.2, lineHeight: 1.35 }}>{value}</Typography>
          </Box>
        ))}
      </Box>
      {!sleep?.lastBedtime && !sleep?.wake ? <Typography sx={{ mt: .85, color: "text.secondary", fontSize: 11.5 }}>Todavía no hay datos suficientes. Decile a Jarvis “me voy a dormir” y el próximo despertar cerrará la sesión si corresponde.</Typography> : null}
    </Box>
  );
}


function KitchenStockPanel({ kitchen, onOpenStock }) {
  const items = kitchen?.items || [];
  const ideas = kitchen?.localIdeas || [];
  return (
    <Box id="owner-life-kitchen" sx={{ ...surfaceSx, p: { xs: 1.35, sm: 1.55 } }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "flex-start" }}>
        <Box>
          <Stack direction="row" spacing={.65} alignItems="center">
            <LocalDiningRoundedIcon fontSize="small" />
            <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Cocina & stock</Typography>
          </Stack>
          <Typography sx={{ mt: .35, color: "text.secondary", fontSize: 12.5, lineHeight: 1.45, maxWidth: 720 }}>
            Jarvis cruza el stock personal con tu Life Map. “Qué tengo para cocinar” se resuelve local; “qué puedo cocinar” usa primero combinaciones conocidas y sólo recurre a IA cuando hace falta.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={onOpenStock} sx={{ ...compactActionSx, whiteSpace: "nowrap" }}>Ver stock</Button>
      </Stack>

      {!items.length ? (
        <Typography sx={{ mt: 1, color: "text.secondary", fontSize: 12.5 }}>
          Todavía no hay alimentos identificados en tu stock. Cuando una compra consumible se suma al stock, Jarvis puede empezar a usarla para pensar comidas.
        </Typography>
      ) : (
        <>
          <Stack direction="row" spacing={.55} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
            {items.slice(0, 14).map((item) => (
              <Chip
                key={item.id || `${item.name}-${item.unit}`}
                size="small"
                label={`${item.name} · ${Number(item.quantity || 0).toLocaleString("es-AR")} ${item.unit || "u"}`}
                variant="outlined"
                sx={{ fontWeight: 800 }}
              />
            ))}
          </Stack>
          {ideas.length ? (
            <Box sx={{ mt: 1.05 }}>
              <Typography sx={{ fontWeight: 950, fontSize: 12.5 }}>Ideas que salen sin gastar IA</Typography>
              <Stack spacing={.55} sx={{ mt: .55 }}>
                {ideas.slice(0, 3).map((idea) => (
                  <Typography key={idea.name} sx={{ fontSize: 12.2, lineHeight: 1.4 }}>
                    <strong>{idea.name}</strong>{idea.uses?.length ? ` · ${idea.uses.join(", ")}` : ""}{idea.note ? ` · ${idea.note}` : ""}
                  </Typography>
                ))}
              </Stack>
            </Box>
          ) : null}
          <Typography sx={{ mt: .95, color: "text.secondary", fontSize: 11.2, lineHeight: 1.4 }}>
            Probá: “qué hay en la heladera”, “qué tengo para cocinar”, “qué me hago de comer” o “tirame ideas con lo que hay”.
          </Typography>
        </>
      )}
    </Box>
  );
}

function emptyForm() {
  return {
    id: "",
    name: "",
    kind: "event",
    weekdays: [...ALL_DAYS],
    targetMinutes: "15",
    sleepTargetHours: "",
    targetCount: "1",
    targetTime: "",
    toleranceMinutes: "30",
    preferredWindow: "any",
    matchTerms: "",
    notes: "",
    active: true,
  };
}

function formFromHabit(habit = {}) {
  return {
    ...emptyForm(),
    id: habit.id || habit._id || "",
    name: habit.name || "",
    kind: habit.kind || "event",
    weekdays: Array.isArray(habit.weekdays) && habit.weekdays.length ? habit.weekdays : [...ALL_DAYS],
    targetMinutes: habit.targetMinutes ? String(habit.targetMinutes) : "15",
    sleepTargetHours: habit.kind === "sleep" && habit.targetMinutes ? String(Math.round((Number(habit.targetMinutes) / 60) * 100) / 100) : "",
    targetCount: habit.targetCount ? String(habit.targetCount) : "1",
    targetTime: habit.targetTime || "",
    toleranceMinutes: habit.toleranceMinutes != null ? String(habit.toleranceMinutes) : "30",
    preferredWindow: habit.preferredWindow || "any",
    matchTerms: Array.isArray(habit.matchTerms) ? habit.matchTerms.join(", ") : "",
    notes: habit.notes || "",
    active: habit.active !== false,
  };
}

function formFromPreset(preset = {}) {
  return {
    ...emptyForm(),
    name: preset.name || preset.title || "",
    kind: preset.habitKind || preset.kind || "duration",
    weekdays: Array.isArray(preset.weekdays) && preset.weekdays.length ? preset.weekdays.map(Number) : [...ALL_DAYS],
    targetMinutes: preset.targetMinutes ? String(preset.targetMinutes) : "15",
    sleepTargetHours: (preset.habitKind || preset.kind) === "sleep" && preset.targetMinutes ? String(Math.round((Number(preset.targetMinutes) / 60) * 100) / 100) : "",
    targetCount: preset.targetCount ? String(preset.targetCount) : "1",
    targetTime: preset.targetTime || "",
    toleranceMinutes: preset.toleranceMinutes != null ? String(preset.toleranceMinutes) : "30",
    preferredWindow: preset.preferredWindow || "any",
    matchTerms: Array.isArray(preset.matchTerms) ? preset.matchTerms.join(", ") : (preset.matchTerms || ""),
    notes: preset.notes || "",
  };
}

function statusMeta(status) {
  if (status === "done") return { label: "Cumplido", color: "success" };
  if (status === "missed") return { label: "Fuera de objetivo", color: "warning" };
  if (status === "off") return { label: "No toca hoy", color: "default" };
  return { label: "Pendiente", color: "default" };
}

function habitTargetLabel(habit) {
  if (habit.kind === "duration") return `${habit.targetMinutes || 0} min`;
  if (habit.kind === "wake_before") return `antes de ${habit.targetTime || "—"}${habit.toleranceMinutes ? ` · +${habit.toleranceMinutes} min` : ""}`;
  if (habit.kind === "count") return `${habit.targetCount || 1} veces`;
  return WINDOW_LABELS[habit.preferredWindow] || "Flexible";
}

function progressValue(habit) {
  if (habit.kind !== "duration") return habit.today?.status === "done" ? 100 : 0;
  const target = Number(habit.today?.target || habit.targetMinutes || 1);
  const value = Number(habit.today?.progress || 0);
  return Math.max(0, Math.min(100, (value / Math.max(1, target)) * 100));
}

function HabitCard({ habit, dateKey, onEdit, onToggleActive, onManualDone }) {
  const meta = statusMeta(habit.today?.status);
  const history = habit.tracking?.history || [];
  return (
    <Box sx={{ ...surfaceSx, p: { xs: 1.35, sm: 1.55 } }}>
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={.6} useFlexGap flexWrap="wrap" alignItems="center">
            <Typography sx={{ fontWeight: 1000, fontSize: 16, letterSpacing: "-.02em" }}>{habit.name}</Typography>
            {!habit.active ? <Chip size="small" label="Pausado" /> : <Chip size="small" label={meta.label} color={meta.color} sx={{ fontWeight: 900 }} />}
          </Stack>
          <Typography sx={{ mt: .35, color: "text.secondary", fontSize: 12.5, fontWeight: 750 }}>
            {KIND_LABELS[habit.kind] || "Hábito"} · {habitTargetLabel(habit)}
          </Typography>
        </Box>
        <Button size="small" onClick={() => onEdit(habit)} sx={{ ...compactActionSx, minWidth: 0 }}>Editar</Button>
      </Stack>

      {habit.active && habit.today?.scheduled ? (
        <Box sx={{ mt: 1.15 }}>
          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
            <Typography sx={{ fontWeight: 900, fontSize: 13.2 }}>{habit.today?.label || meta.label}</Typography>
            {habit.today?.evidence?.source && habit.today?.evidence?.source !== "manual" ? (
              <Chip size="small" icon={<AutoAwesomeRoundedIcon />} label="Detectado" variant="outlined" sx={{ fontWeight: 850 }} />
            ) : null}
          </Stack>
          {habit.kind === "duration" ? <LinearProgress variant="determinate" value={progressValue(habit)} sx={{ mt: .7, height: 7, borderRadius: 999 }} /> : null}
        </Box>
      ) : null}

      <Stack direction="row" spacing={.45} alignItems="center" sx={{ mt: 1.2 }}>
        {(history || []).slice(-7).map((row) => (
          <Box
            key={row.dateKey}
            title={`${row.dateKey}: ${statusMeta(row.status).label}`}
            sx={{ width: 18, height: 7, borderRadius: 999, bgcolor: row.status === "done" ? "success.main" : row.status === "missed" ? "warning.main" : row.scheduled ? "action.selected" : "divider" }}
          />
        ))}
        <Typography sx={{ ml: .6, color: "text.secondary", fontSize: 11.5, fontWeight: 800 }}>
          {habit.tracking?.scheduled ? `${habit.tracking.completed}/${habit.tracking.scheduled} últimos ${habit.tracking.days} días` : "Sin historial todavía"}
        </Typography>
      </Stack>

      <Divider sx={{ my: 1.05 }} />
      <Stack direction="row" spacing={.7} useFlexGap flexWrap="wrap">
        {habit.active && habit.today?.scheduled && habit.today?.status !== "done" ? (
          <Button size="small" startIcon={<CheckCircleRoundedIcon />} onClick={() => onManualDone(habit, dateKey)} sx={compactActionSx}>Marcar manualmente</Button>
        ) : null}
        <Button
          size="small"
          startIcon={habit.active ? <PauseCircleOutlineRoundedIcon /> : <PlayCircleOutlineRoundedIcon />}
          onClick={() => onToggleActive(habit)}
          sx={compactActionSx}
        >
          {habit.active ? "Pausar" : "Activar"}
        </Button>
      </Stack>
    </Box>
  );
}

export default function OwnerHabitsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [form, setForm] = React.useState(emptyForm);
  const [fromJarvis, setFromJarvis] = React.useState(false);
  const appliedTokenRef = React.useRef("");

  const load = React.useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const habitResponse = await ownerApi.habits({ historyDays: 7 });
      setSnapshot(habitResponse?.snapshot || null);
    } catch (err) {
      setError(formatApiError(err, "No pude cargar tus hábitos."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const handler = () => load();
    window.addEventListener("owner:jarvis-mutated", handler);
    return () => window.removeEventListener("owner:jarvis-mutated", handler);
  }, [load]);

  React.useEffect(() => {
    const ui = location.state?.ownerJarvisUi || null;
    const queryNew = new URLSearchParams(location.search).get("jarvisAction") === "new";
    if (!queryNew) return;
    const token = ui?.token || `query:${location.key || location.search}`;
    if (appliedTokenRef.current === token) return;
    appliedTokenRef.current = token;
    setForm(ui?.preset ? formFromPreset(ui.preset) : emptyForm());
    setFromJarvis(Boolean(ui?.token));
    setDialogOpen(true);
  }, [location.key, location.search, location.state]);

  const openNew = () => {
    setForm(emptyForm());
    setFromJarvis(false);
    setDialogOpen(true);
  };
  const openEdit = (habit) => {
    setForm(formFromHabit(habit));
    setFromJarvis(false);
    setDialogOpen(true);
  };
  const openSleep = () => {
    if (snapshot?.sleep?.habit) return openEdit(snapshot.sleep.habit);
    setForm({ ...emptyForm(), name: "Sueño", kind: "sleep", preferredWindow: "night", targetMinutes: "", sleepTargetHours: "", targetTime: "" });
    setFromJarvis(false);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    if (new URLSearchParams(location.search).get("jarvisAction")) navigate("/dashboard/owner/habitos", { replace: true, state: {} });
  };

  const toggleDay = (day) => setForm((current) => {
    const has = current.weekdays.includes(day);
    const weekdays = has ? current.weekdays.filter((value) => value !== day) : [...current.weekdays, day].sort();
    return { ...current, weekdays: weekdays.length ? weekdays : current.weekdays };
  });

  const save = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        kind: form.kind,
        weekdays: form.weekdays,
        preferredWindow: form.preferredWindow,
        targetMinutes: form.kind === "duration" ? Number(form.targetMinutes || 0) : form.kind === "sleep" && Number(form.sleepTargetHours || 0) > 0 ? Math.round(Number(form.sleepTargetHours) * 60) : null,
        targetCount: form.kind === "count" ? Number(form.targetCount || 1) : null,
        targetTime: ["wake_before", "sleep"].includes(form.kind) ? (form.targetTime || null) : null,
        toleranceMinutes: form.kind === "wake_before" ? Number(form.toleranceMinutes || 0) : 30,
        matchTerms: form.matchTerms.split(/[,;]+/).map((value) => value.trim()).filter(Boolean),
        notes: form.notes.trim(),
        source: fromJarvis ? "jarvis" : "manual",
      };
      if (form.id) await ownerApi.updateHabit(form.id, payload);
      else await ownerApi.createHabit(payload);
      closeDialog();
      await load();
      try { window.dispatchEvent(new CustomEvent("owner:jarvis-mutated", { detail: { scopes: ["today", "habits"] } })); } catch { /* noop */ }
    } catch (err) {
      setError(formatApiError(err, "No pude guardar el hábito."));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (habit) => {
    try {
      await ownerApi.updateHabit(habit.id || habit._id, { active: habit.active === false });
      await load();
    } catch (err) { setError(formatApiError(err, "No pude cambiar el estado del hábito.")); }
  };

  const manualDone = async (habit, dateKey) => {
    try {
      await ownerApi.setHabitCheckin(habit.id || habit._id, dateKey, true, { source: "manual" });
      await load();
    } catch (err) { setError(formatApiError(err, "No pude marcar el hábito.")); }
  };

  const canSave = Boolean(form.name.trim())
    && (form.kind !== "duration" || Number(form.targetMinutes || 0) > 0)
    && (form.kind !== "wake_before" || Boolean(form.targetTime))
    && (form.kind !== "sleep" || Boolean(form.targetTime) || Number(form.sleepTargetHours || 0) > 0);

  const visibleHabits = (snapshot?.habits || []).filter((row) => !["sleep", "wake_before"].includes(String(row.kind || "")));
  const activeHabits = visibleHabits.filter((row) => row.active !== false);
  const pausedHabits = visibleHabits.filter((row) => row.active === false);
  const summary = {
    total: activeHabits.filter((row) => row.today?.scheduled).length,
    done: activeHabits.filter((row) => row.today?.status === "done").length,
    pending: activeHabits.filter((row) => row.today?.status === "pending").length,
    missed: activeHabits.filter((row) => row.today?.status === "missed").length,
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto", pb: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.2} alignItems={{ sm: "flex-start" }} sx={{ mb: 1.2 }}>
        <Box>
          <Typography sx={{ color: "text.secondary", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".09em" }}>Life Map</Typography>
          <Typography component="h1" sx={{ mt: .15, fontSize: { xs: "1.7rem", sm: "2.15rem" }, fontWeight: 1000, letterSpacing: "-.045em" }}>Hábitos</Typography>
          <Typography sx={{ mt: .35, color: "text.secondary", maxWidth: 720, fontSize: 13.5, lineHeight: 1.45 }}>
            Tus comportamientos repetibles viven acá. Sueño y Comida tienen superficies propias para no mezclar señales distintas.
          </Typography>
        </Box>
        <Stack direction="row" spacing={.65}>
          <Button variant="outlined" onClick={() => navigate("/dashboard/owner/sueno")} sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2.3 }}>Sueño</Button>
          <Button variant="outlined" onClick={() => navigate("/dashboard/owner/comida")} sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2.3 }}>Comida</Button>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openNew} sx={{ textTransform: "none", fontWeight: 950, borderRadius: 2.3 }}>Nuevo hábito</Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 1.1 }}>{error}</Alert> : null}
      {loading ? <Box sx={{ py: 9, textAlign: "center" }}><CircularProgress size={30} /></Box> : null}

      {!loading ? (
        <Stack spacing={1.15}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(4,minmax(0,1fr))" }, gap: .8 }}>
            {[
              ["Hoy", summary.total, <TrackChangesRoundedIcon fontSize="small" />],
              ["Cumplidos", summary.done, <CheckCircleRoundedIcon fontSize="small" />],
              ["Pendientes", summary.pending, <ScheduleRoundedIcon fontSize="small" />],
              ["Fuera de eje", summary.missed, <BedtimeRoundedIcon fontSize="small" />],
            ].map(([label, value, icon]) => (
              <Box key={label} sx={{ ...surfaceSx, p: 1.2 }}>
                <Stack direction="row" spacing={.55} alignItems="center" sx={{ color: "text.secondary" }}>{icon}<Typography sx={{ fontSize: 11, fontWeight: 900 }}>{label}</Typography></Stack>
                <Typography sx={{ mt: .35, fontSize: 26, fontWeight: 1000, letterSpacing: "-.04em" }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          {!activeHabits.length ? (
            <Box sx={{ ...surfaceSx, p: { xs: 2, sm: 2.5 }, textAlign: "center" }}>
              <Typography sx={{ fontWeight: 1000, fontSize: 17 }}>Todavía no hay hábitos activos.</Typography>
              <Typography sx={{ mt: .45, color: "text.secondary", fontSize: 13.5 }}>Podés crear uno acá o decirle a Jarvis: “quiero leer 15 minutos todos los días”.</Typography>
              <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={openNew} sx={{ mt: 1.2, textTransform: "none", fontWeight: 900 }}>Crear el primero</Button>
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))" }, gap: 1 }}>
              {activeHabits.map((habit) => <HabitCard key={habit.id || habit._id} habit={habit} dateKey={snapshot?.dateKey} onEdit={openEdit} onToggleActive={toggleActive} onManualDone={manualDone} />)}
            </Box>
          )}

          {pausedHabits.length ? (
            <Box sx={{ ...surfaceSx, p: 1.25 }}>
              <Typography sx={{ fontWeight: 950, fontSize: 13 }}>Pausados</Typography>
              <Stack direction="row" spacing={.65} useFlexGap flexWrap="wrap" sx={{ mt: .75 }}>
                {pausedHabits.map((habit) => <Chip key={habit.id || habit._id} label={habit.name} onClick={() => openEdit(habit)} onDelete={() => toggleActive(habit)} deleteIcon={<PlayCircleOutlineRoundedIcon />} />)}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      ) : null}

      <Dialog open={dialogOpen} onClose={saving ? undefined : closeDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: .7, fontWeight: 1000 }}>{form.id ? "Editar hábito" : "Nuevo hábito"}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: .4 }}>
            {fromJarvis ? <Alert severity="info" icon={<AutoAwesomeRoundedIcon />}>Jarvis interpretó tu frase y dejó este hábito preparado. Revisalo antes de guardar.</Alert> : null}
            <TextField label="Qué querés incorporar" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} autoFocus fullWidth placeholder="Ej. Leer" />
            <FormControl fullWidth>
              <InputLabel>Cómo se cumple</InputLabel>
              <Select label="Cómo se cumple" value={form.kind} onChange={(event) => setForm((current) => ({ ...current, kind: event.target.value }))}>
                <MenuItem value="duration">Acumular minutos</MenuItem>
                <MenuItem value="event">Que ocurra una vez</MenuItem>
                <MenuItem value="count">Una cantidad de veces</MenuItem>
              </Select>
            </FormControl>
            {form.kind === "duration" ? <TextField label="Minutos por día" type="number" value={form.targetMinutes} onChange={(event) => setForm((current) => ({ ...current, targetMinutes: event.target.value }))} inputProps={{ min: 1, max: 1440 }} /> : null}
            {form.kind === "count" ? <TextField label="Veces por día" type="number" value={form.targetCount} onChange={(event) => setForm((current) => ({ ...current, targetCount: event.target.value }))} inputProps={{ min: 1, max: 100 }} /> : null}
            {form.kind === "wake_before" ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField fullWidth label="Quiero estar levantado antes de" type="time" value={form.targetTime} onChange={(event) => setForm((current) => ({ ...current, targetTime: event.target.value }))} InputLabelProps={{ shrink: true }} />
                <TextField fullWidth label="Tolerancia (min)" type="number" value={form.toleranceMinutes} onChange={(event) => setForm((current) => ({ ...current, toleranceMinutes: event.target.value }))} inputProps={{ min: 0, max: 240 }} />
              </Stack>
            ) : form.kind === "sleep" ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField fullWidth label="Hora objetivo de acostarme (opcional)" type="time" value={form.targetTime} onChange={(event) => setForm((current) => ({ ...current, targetTime: event.target.value }))} InputLabelProps={{ shrink: true }} />
                <TextField fullWidth label="Horas objetivo de sueño (opcional)" type="number" value={form.sleepTargetHours} onChange={(event) => setForm((current) => ({ ...current, sleepTargetHours: event.target.value }))} inputProps={{ min: .25, max: 20, step: .25 }} helperText="Podés configurar sólo horario, sólo duración o ambos." />
              </Stack>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Momento preferido</InputLabel>
                <Select label="Momento preferido" value={form.preferredWindow} onChange={(event) => setForm((current) => ({ ...current, preferredWindow: event.target.value }))}>
                  {Object.entries(WINDOW_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <Box>
              <Typography sx={{ mb: .55, color: "text.secondary", fontSize: 11.5, fontWeight: 900 }}>DÍAS</Typography>
              <Stack direction="row" spacing={.55} useFlexGap flexWrap="wrap">
                {ALL_DAYS.map((day) => <Chip key={day} label={DAY_LABELS[day]} clickable color={form.weekdays.includes(day) ? "primary" : "default"} variant={form.weekdays.includes(day) ? "filled" : "outlined"} onClick={() => toggleDay(day)} sx={{ fontWeight: 900 }} />)}
              </Stack>
            </Box>
            <TextField label="Cómo puede reconocerlo Jarvis (opcional)" value={form.matchTerms} onChange={(event) => setForm((current) => ({ ...current, matchTerms: event.target.value }))} placeholder="Ej. leer, leyendo, lectura" helperText="Si lo dejás vacío, Jarvis infiere palabras desde el nombre." fullWidth />
            <TextField label="Nota (opcional)" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} multiline minRows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.4, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: "none", fontWeight: 900 }}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={saving || !canSave} sx={{ textTransform: "none", fontWeight: 950 }}>{saving ? "Guardando…" : "Guardar hábito"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
